import React from 'react';
import {
  LayoutDashboard, InboxIcon, Lightbulb, ClipboardList, Video, Calendar,
  Film, Scissors, Eye, CheckSquare, Send, Layers,
  BookOpen, Archive, Users, Bell, Settings, ChevronRight, Sparkles,
} from 'lucide-react';

/**
 * Left navigation sidebar for the Marketing & Media Command Centre.
 * Grouped so we don't overwhelm users, but every section is one click away.
 */
export const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { v: 'dashboard', l: 'Dashboard', icon: LayoutDashboard },
      { v: 'my-work', l: 'My Assigned Work', icon: InboxIcon, showMyCount: true },
      { v: 'notifications', l: 'Notifications', icon: Bell, showNotifCount: true },
    ],
  },
  {
    label: 'Intake',
    items: [
      { v: 'ideas', l: 'Ideas Board', icon: Lightbulb },
      { v: 'requests', l: 'Marketing Requests', icon: ClipboardList },
    ],
  },
  {
    label: 'Production',
    items: [
      { v: 'filming', l: 'Filming Requests', icon: Video },
      { v: 'filming-calendar', l: 'Filming Calendar', icon: Calendar },
      { v: 'raw-footage', l: 'Raw Footage', icon: Film },
      { v: 'editing-queue', l: 'Editing Queue', icon: Scissors },
      { v: 'reviews', l: 'Content Reviews', icon: Eye },
      { v: 'approvals', l: 'Approvals', icon: CheckSquare },
      { v: 'publishing', l: 'Publishing Schedule', icon: Send },
    ],
  },
  {
    label: 'Platforms',
    items: [
      { v: 'platforms', l: 'All Platforms', icon: Layers },
    ],
  },
  {
    label: 'Library',
    items: [
      { v: 'hooks', l: 'Hook Library', icon: Sparkles },
      { v: 'completed', l: 'Completed Content', icon: Archive },
    ],
  },
  {
    label: 'Management',
    items: [
      { v: 'team-view', l: 'Team View', icon: Users },
      { v: 'settings', l: 'Settings', icon: Settings },
    ],
  },
];

export default function MccSidebar({ active, onSelect, myWorkCount = 0, unreadNotifs = 0, collapsed, onToggle }) {
  return (
    <aside
      className={`${collapsed ? 'w-16' : 'w-60'} bg-slate-900 text-slate-300 flex-shrink-0 flex flex-col border-r border-slate-800 transition-all select-none`}
    >
      <div className="p-3 flex items-center justify-between border-b border-slate-800">
        {!collapsed && (
          <div>
            <p className="text-[10px] font-bold text-indigo-400 tracking-[0.2em] uppercase">Derwent</p>
            <p className="text-sm font-bold text-white leading-tight">Marketing & Media</p>
          </div>
        )}
        <button
          onClick={onToggle}
          className="text-slate-400 hover:text-white p-1 rounded"
          data-testid="mcc-sidebar-toggle"
        >
          <ChevronRight className={`w-4 h-4 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="mb-2">
            {!collapsed && (
              <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-500 px-4 mb-1">{group.label}</p>
            )}
            {group.items.map((it) => {
              const Icon = it.icon;
              const isActive = active === it.v;
              const count = it.showMyCount ? myWorkCount : it.showNotifCount ? unreadNotifs : 0;
              return (
                <button
                  key={it.v}
                  onClick={() => onSelect(it.v)}
                  data-testid={`mcc-nav-${it.v}`}
                  className={`w-full flex items-center gap-2.5 px-4 py-2 text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-indigo-600 text-white'
                      : 'text-slate-300 hover:bg-indigo-500/20 hover:text-white'
                  }`}
                  title={collapsed ? it.l : ''}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {!collapsed && <span className="flex-1 text-left">{it.l}</span>}
                  {!collapsed && count > 0 && (
                    <span className={`px-1.5 rounded text-[9px] font-bold ${isActive ? 'bg-white text-indigo-700' : 'bg-indigo-500 text-white'}`}>
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {!collapsed && (
        <div className="border-t border-slate-800 p-3 text-[10px] text-slate-500">
          <p>Derwent Finance CRM</p>
          <p className="text-slate-600">Media Command Centre v2</p>
        </div>
      )}
    </aside>
  );
}
